import { safePathEncode } from '@/utils/safePath'
import { apiSlice } from './apiSlice'
import type { LookupItem, SaleContractPayload } from './saleContractApi'

export interface ContractSnapshot {
  contractNo: string
  contractType?: string | null
  contractQuality?: string | null
  contractQuantity?: number | null
  contractPriceUnit?: string | null
  contractOutrightPrice?: number | null
  contractPtbfDiffs?: number | null
  contractClientName?: string | null
  contractDate?: string | null
  contractBags?: number | null
}

export interface SaleAmendment {
  id?: number | null
  amendmentNo: string
  amendmentDate: string
  contractNo: string
  clientCode?: string | null
  clientName?: string | null
  clientShortName?: string | null
  clientAddress?: string | null
  clientPhone?: string | null
  clientFax?: string | null
  clientVatCode?: string | null
  clientRepresentative?: string | null
  clientPosition?: string | null
  contractDate?: string | null
  contentBeforeChange?: string | null
  contentAfterChange?: string | null
  note?: string | null
  price?: number | null
  quantity?: number | null
  dateUpdate?: string | null
  personUpdate?: string | null
  createdDate?: string | null
  createdBy?: string | null
  updatedDate?: string | null
  updatedBy?: string | null
  isStatus?: number | null
  contractSnapshot?: ContractSnapshot | null
}

export type SaleAmendmentPayload = Pick<
  SaleAmendment,
  | 'amendmentNo'
  | 'amendmentDate'
  | 'contractNo'
  | 'contentBeforeChange'
  | 'contentAfterChange'
  | 'note'
  | 'price'
  | 'quantity'
>

export interface SaleAmendmentListResponse {
  items: SaleAmendment[]
  total: number
  page: number
  pageSize: number
}

export interface SaleAmendmentListQuery {
  page: number
  pageSize: number
  q?: string
}

export interface SaleAmendmentPrintResponse {
  title: string
  html: string
  message?: string
}

export interface SaleAmendmentSeparationResponse {
  sourceContractNo: string
  amendmentNo: string
  mode: string
  draft: SaleContractPayload
  brokerFirm?: string | null
  traderName?: string | null
  fieldActions: Array<{ field: string; action: string; source?: string }>
}

export interface SaleAmendmentContractOptionsResponse {
  items: LookupItem[]
  total: number
}

const saleAmendmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleAmendments: builder.query<SaleAmendmentListResponse, SaleAmendmentListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/sale-amendments',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['SaleAmendment'],
    }),

    getSaleAmendment: builder.query<SaleAmendment, string>({
      query: (amendmentNo) => `/sale-amendments/${safePathEncode(amendmentNo)}`,
      providesTags: (_r, _e, amendmentNo) => [{ type: 'SaleAmendment', id: amendmentNo }],
    }),

    createSaleAmendment: builder.mutation<SaleAmendment, SaleAmendmentPayload>({
      query: (body) => ({ url: '/sale-amendments', method: 'POST', body }),
      invalidatesTags: ['SaleAmendment'],
    }),

    updateSaleAmendment: builder.mutation<SaleAmendment, { amendmentNo: string; body: SaleAmendmentPayload }>({
      query: ({ amendmentNo, body }) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SaleAmendment'],
    }),

    cancelSaleAmendment: builder.mutation<SaleAmendment, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['SaleAmendment'],
    }),

    getSaleAmendmentContracts: builder.query<SaleAmendmentContractOptionsResponse, string | void>({
      query: (q) => ({ url: '/sale-amendments/contracts', params: q ? { q } : {} }),
    }),

    getSaleAmendmentContractSnapshot: builder.query<ContractSnapshot, string>({
      query: (contractNo) => `/sale-amendments/contracts/${safePathEncode(contractNo)}/snapshot`,
    }),

    printSaleAmendmentEn: builder.mutation<SaleAmendmentPrintResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/print/en`,
        method: 'POST',
      }),
    }),

    printSaleAmendmentVn: builder.mutation<SaleAmendmentPrintResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/print/vn`,
        method: 'POST',
      }),
    }),

    separateSaleAmendmentContract: builder.mutation<SaleAmendmentSeparationResponse, string>({
      query: (amendmentNo) => ({
        url: `/sale-amendments/${safePathEncode(amendmentNo)}/contract-separation`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useGetSaleAmendmentsQuery,
  useGetSaleAmendmentQuery,
  useCreateSaleAmendmentMutation,
  useUpdateSaleAmendmentMutation,
  useCancelSaleAmendmentMutation,
  useLazyGetSaleAmendmentContractsQuery,
  useGetSaleAmendmentContractSnapshotQuery,
  usePrintSaleAmendmentEnMutation,
  usePrintSaleAmendmentVnMutation,
  useSeparateSaleAmendmentContractMutation,
} = saleAmendmentsApi

export default saleAmendmentsApi
